/**
 * Sending the sales an offline till kept, once the line is back.
 *
 * One at a time, oldest first, each in its own request: the server records a
 * sale whole or not at all (`fiscal.offline`), so one it refuses does not hold
 * up the rest. Sending one twice is safe — it is keyed by the sale's own id —
 * so a send whose answer was lost is simply sent again.
 *
 * A refusal (the server answered, and said no) is kept with its reason for a
 * person to look at. A lost connection stops the run, to try again later.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ApiError, sendOfflineSale, useApi } from '@restaurant/api-client';

import type { Connection } from './connection';
import { dequeue, loadQueue, markRefused, onQueueChange, type QueuedSale } from './store';

/** The queue as it stands, kept fresh for the screens that show it. */
export function useQueue(): QueuedSale[] {
  const [queue, setQueue] = useState<QueuedSale[]>([]);
  const version = useSyncExternalStore(onQueueChange, () => versionCounter.value);
  useEffect(() => {
    let alive = true;
    void loadQueue().then((q) => {
      if (alive) setQueue(q);
    });
    return () => {
      alive = false;
    };
  }, [version]);
  return queue;
}

// Bumped on every change, so `useSyncExternalStore` sees a new snapshot.
const versionCounter = { value: 0 };
onQueueChange(() => {
  versionCounter.value += 1;
});

/** Send what is waiting whenever the till is online. */
export function useSync(connection: Connection): { sending: boolean; sendNow: () => void } {
  const api = useApi();
  const [sending, setSending] = useState(false);
  const running = useRef(false);

  const sendNow = useCallback(() => {
    if (running.current) return;
    running.current = true;
    setSending(true);
    void (async () => {
      try {
        for (const queued of await loadQueue()) {
          if (queued.refused) continue;
          try {
            await sendOfflineSale(api, queued.sale);
            await dequeue(queued.sale.client_id);
          } catch (error) {
            if (
              error instanceof ApiError &&
              error.status >= 400 &&
              error.status < 500 &&
              error.status !== 401 &&
              error.status !== 429
            ) {
              await markRefused(queued.sale.client_id, error.message);
              continue;
            }
            // No answer, or a server in trouble: try the lot again later.
            break;
          }
        }
      } finally {
        running.current = false;
        setSending(false);
      }
    })();
  }, [api]);

  useEffect(() => {
    if (connection === 'online') sendNow();
  }, [connection, sendNow]);

  return { sending, sendNow };
}
